import React from 'react';

import { useTheme } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListSubheader from '@mui/material/ListSubheader';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import Divider from '@mui/material/Divider';

import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import ICatalogItem from '../../catalog/ICatalogItem';
import { CatalogContext } from '../../catalog/CatalogHandler';
import Box from "@mui/material/Box";
import SideMenuIconButton from './SideMenuIconButton';

import './InstantCatalog.css';
import Typography from "@mui/material/Typography";

interface CatalogItemProps {
    itemIndex: number;
    item: ICatalogItem;
    depth: number;
    initialExpandedIds: Set<number>;
    selectItem: (item: ICatalogItem) => void;
}

/** Componenet for displaying an instant catalog item content */
const CatalogItem: React.FC<CatalogItemProps> = ({ ...props }) => {
    const theme = useTheme();
    const [open, setOpen] = React.useState(props.initialExpandedIds.has(props.item.CategoryID));
    const [imgLoaded, setImgLoaded] = React.useState(false);
    const [imgError, setImgError] = React.useState(false);

    const toggleOpen = () => {
        setOpen(!open);
    };

    return (
        <>
            <ListItemButton
                onClick={props.item.Children !== undefined ? toggleOpen : () => props.selectItem(props.item)}
                sx={{ pl: (props.depth * 2) + 2 }}
            >
                <Box  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    {/* Only show avatar if no children; otherwise show expand/collapse icon */}
                    {props.item.Children === undefined && (
                        <ListItemAvatar>
                            {/* Placeholder — always rendered, imidiately visible till the image not loaded */}
                            {!imgLoaded && (
                                <div
                                    id={`catalogthumb-${props.itemIndex}-${props.depth}-${props.item.CategoryID}`}
                                    style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '10%',
                                        backgroundColor: '#ccc',
                                    }}
                                />
                            )}
                            {/* Image — loading in background, visible only on succeed */}
                            {!imgError && (
                                <img
                                    src={props.item.Image}
                                    alt={props.item.Name}
                                    style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '10%',
                                        display: imgLoaded ? 'block' : 'none',
                                    }}
                                    onLoad={() => setImgLoaded(true)}
                                    onError={() => setImgError(true)}
                                />
                            )}
                        </ListItemAvatar>
                    )}
                    <ListItemText primary={props.item.Name} slotProps={{ primary: { fontWeight: 'bold', color: theme.palette.text.blueish} }} />
                    {props.item.Children !== undefined && (
                        <SideMenuIconButton
                            isOpen={open}
                            onClick={e => { e.stopPropagation(); toggleOpen(); }}
                            size="small"
                            ariaLabel={open ? 'Collapse' : 'Expand'}
                        />
                    )}
                </Box>
            </ListItemButton>
            <Divider sx={{ display: open ? 'none' : 'block' }} />
            {props.item.Children !== undefined && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <Divider />
                    <List component="div" disablePadding>
                        {props.item.Children.map((child: ICatalogItem, index) => (
                            <CatalogItem
                                key={index}
                                itemIndex={index}
                                item={child}
                                depth={props.depth + 1}
                                initialExpandedIds={props.initialExpandedIds}
                                selectItem={props.selectItem}
                            />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
};

interface InstantCatalogProps {
}

/**
 * Component handling the instant catalog content
 */
const InstantCatalog: React.FC<InstantCatalogProps> = ({ ...props }) => {
    const theme = useTheme();
    const catalogContext = React.useContext(CatalogContext);

    const [open, setOpen] = React.useState(false);

    const toggleDrawer = (newOpen: boolean) => () => {
        setOpen(newOpen);
    };

    // Filter catalog to only include items with products, hide empty categories recursively
    const filteredCatalogItems = React.useMemo(
        () => {
            const filterItems = (items: ICatalogItem[]): ICatalogItem[] => {
                return items.map(item => {
                    if (item.Children) {
                        const filteredChildren = filterItems(item.Children);
                        return { ...item, Children: filteredChildren };
                    }
                    return item;
                }).filter(item => item.Children !== undefined ? item.Children.length > 0 : true);
            };

            return catalogContext.catalog ? filterItems(catalogContext.catalog.children()) : [];
        }
    , [catalogContext.catalog]);

    return (
        <Container style={{ backgroundColor: "#314c3d", position: 'fixed', paddingTop: 10, paddingBottom: 10, top: 16, width: 'auto', zIndex: 1000 /* Behind drawer */ }}>
            <Box
                // startIcon={<AutoStoriesIcon />}
                style={{cursor: 'pointer', display: 'flex'}}
                onClick={toggleDrawer(true)}
            >
                <img
                    src={'/modules/ubercabinet/views/images/catalog.svg'}
                    style={{marginRight: 10}}
                />
                <Typography sx={{}}>Katalógus</Typography>
            </Box>
            <Drawer
                sx={{ backgroundColor: 'transparent' }}
                open={open}
                onClose={toggleDrawer(false)}
                keepMounted
                slotProps={{
                    paper: {
                        sx: {
                            width: 350,
                            maxWidth: '85vw',
                        }
                    }
                }}
            >
                <List
                    sx={{ backgroundColor: 'transparent', width: '100%' }}
                    subheader={
                    <ListSubheader>
                        <Button
                            variant="contained"
                            startIcon={<ChevronLeftIcon />}
                            onClick={toggleDrawer(false)}>
                                Bezár
                        </Button>
                    </ListSubheader>
                }>
                    {filteredCatalogItems.map((item: ICatalogItem, index) =>
                        <CatalogItem
                            key={index}
                            itemIndex={index}
                            item={item}
                            depth={0}
                            initialExpandedIds={catalogContext.initialExpandedIds}
                            selectItem={(selectedItem: ICatalogItem) => {
                                catalogContext.selectItem(selectedItem);
                                toggleDrawer(false)();
                            }}
                        />
                    )}
                </List>
            </Drawer>
        </Container>
    );
}

export default InstantCatalog;
